import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlarmManagementModel } from '@models/alarm-management.model';
import { AlertsActivity, AlertActivityType } from '@models/alerts-v2.model';
import { alertActivityTypeMap } from '@consts/alerts-v2.const';
import { AlarmManagementService } from '../../../services/alarm-management.service';
import { AlertsService } from '../../../development/alerts.service';
import { UiUserSettingsDateComponent } from '../../../shared/ui-kit/ui-user-settings-date/ui-user-settings-date.component';
import { AttachmentViewerDialogComponent } from '../attachment-viewer-dialog/attachment-viewer-dialog.component';
import { ImagePreviewDialogComponent } from '../image-preview-dialog/image-preview-dialog.component';
import { StorageBucket } from '@consts/storage-url.util';
import { UiUserSettingsDateType } from '@enums/shared.enum';
import { AuthSrcDirective } from '../../../shared/directives/auth-src.directive';
import { ThumbnailsService } from '../../../development/thumbnails.service';
import { UiButtonComponent } from '@ui';

@Component({
  selector: 'app-alert-details-dialog',
  standalone: true, imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatTabsModule, MatProgressSpinnerModule, UiUserSettingsDateComponent, AuthSrcDirective, UiButtonComponent],
  templateUrl: './alert-details-dialog.component.html',
  styleUrls: ['./alert-details-dialog.component.scss'],
})
export class AlertDetailsDialogComponent implements OnInit {
  alert: AlarmManagementModel.RejectedAlert;
  activityLogs: AlertsActivity[] = [];
  noonlightLogs: AlarmManagementModel.NoonlightActivityLog[] = [];
  isLoadingActivity = false;
  isLoadingNoonlight = false;
  public thumbnailLoadFailed = false;
  public UiUserSettingsDateType = UiUserSettingsDateType;

  // Make alertActivityTypeMap available to template
  alertActivityTypeMap = alertActivityTypeMap;

  constructor(
    public dialogRef: MatDialogRef<AlertDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlarmManagementModel.AlertDetailsDialogData,
    private alarmManagementService: AlarmManagementService,
    private alertsService: AlertsService,
    private thumbnailsService: ThumbnailsService,
    private dialog: MatDialog
  ) {
    this.alert = data.alert;
  }

  ngOnInit() {
    this.loadActivityLogs();
    this.loadNoonlightLogs();
  }

  loadActivityLogs() {
    this.isLoadingActivity = true;

    // Use new v1 API that takes alertInstanceId directly (no MongoDB lookup needed)
    const alertInstanceId = String(this.alert.alertInstanceId);
    this.alertsService.getActivityByInstanceId(alertInstanceId).subscribe({
      next: (logs) => {
        this.activityLogs = logs;
        this.isLoadingActivity = false;
      },
      error: () => {
        this.isLoadingActivity = false;
        this.activityLogs = [];
      },
    });
  }

  loadNoonlightLogs() {
    this.isLoadingNoonlight = true;
    this.alarmManagementService.getAlertNoonlightActivity(this.alert.alertInstanceId).subscribe({
      next: (logs) => {
        this.noonlightLogs = logs;
        this.isLoadingNoonlight = false;
      },
      error: () => {
        this.isLoadingNoonlight = false;
      },
    });
  }

  getActionLabel(action: AlertActivityType): string {
    return alertActivityTypeMap[action] || `Action ${action}`;
  }

  getActionIcon(action: AlertActivityType): string {
    const icons: { [key: number]: string } = {
      [AlertActivityType.Notify]: 'notifications',
      [AlertActivityType.ACK]: 'check_circle',
      [AlertActivityType.UNACK]: 'remove_circle',
      [AlertActivityType.REST_API]: 'api',
      [AlertActivityType.GPIO]: 'settings_input_component',
      [AlertActivityType.SYSLOG]: 'list_alt',
      [AlertActivityType.PLAY_SOUND]: 'volume_up',
      [AlertActivityType.SEND_CALL_CENTER_IMAGE]: 'image',
      [AlertActivityType.RESPONSE_CALL_CENTER_IMAGE]: 'reply',
      [AlertActivityType.SEND_CALL_CENTER_VIDEO]: 'videocam',
      [AlertActivityType.RESPONSE_CALL_CENTER_VIDEO]: 'reply',
      [AlertActivityType.SEND_DISPATCH_REQUEST]: 'local_police',
      [AlertActivityType.DISPATCH_UPDATE_STATUS]: 'update',
      [AlertActivityType.UPDATE_ALARM_CALL_CENTER]: 'alarm',
      [AlertActivityType.SEND_WEBHOOK]: 'webhook',
      [AlertActivityType.SEND_SLACK]: 'chat',
      [AlertActivityType.SEND_TEAM3]: 'groups',
      [AlertActivityType.SEND_IMMIX]: 'security',
      [AlertActivityType.SET_VARIABLE]: 'code',
      [AlertActivityType.SEND_MICROSOFT_TEAMS]: 'groups',
      [AlertActivityType.UPLOAD_VIDEO]: 'cloud_upload',
      [AlertActivityType.ADD_DISPATCH_CONTACT]: 'person_add',
      [AlertActivityType.AUTO_ACK]: 'schedule',
    };
    return icons[action] || 'info';
  }

  getOperationTypeLabel(operationType: string): string {
    const labels: { [key: string]: string } = {
      'VERIFICATION_REQUEST': 'Verification Request',
      'VERIFICATION_RESPONSE': 'Verification Response',
      'DISPATCH_REQUEST': 'Dispatch Request',
      'DISPATCH_RESPONSE': 'Dispatch Response',
    };
    return labels[operationType] || operationType;
  }

  isRequestType(operationType: string): boolean {
    const requestTypes = ['verification_request', 'VERIFICATION_REQUEST', 'dispatch_request', 'DISPATCH_REQUEST', 'image_upload', 'video_upload'];
    return requestTypes.includes(operationType);
  }

  isResponseType(operationType: string): boolean {
    const responseTypes = ['verification_response', 'VERIFICATION_RESPONSE', 'dispatch_response', 'DISPATCH_RESPONSE', 'event_update'];
    return responseTypes.includes(operationType);
  }

  close() {
    this.dialogRef.close();
  }

  openAttachment(url: string) {
    this.dialog.open(AttachmentViewerDialogComponent, {
      data: { url },
      width: '700px',
      maxHeight: '80vh',
      autoFocus: false,
    });
  }

  getThumbUrl(path: string) {
    const baseUrl = this.thumbnailsService.getBucketBaseUrl(StorageBucket.Thumbnails, true);
    return `${baseUrl}/thumbnails/${path}`;
  }

  public onDialogThumbnailError(): void {
    this.thumbnailLoadFailed = true;
  }

  openImagePreview(thumbPath: string, event: MouseEvent) {
    event.stopPropagation();
    this.dialog.open(ImagePreviewDialogComponent, {
      data: { url: this.getThumbUrl(thumbPath) } as AlarmManagementModel.ImagePreviewDialogData,
      panelClass: 'image-preview-dialog-panel',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
    });
  }
}
